import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { BookOpen, LogOut, ArrowLeft, GraduationCap, Trophy, ExternalLink, Video, User } from 'lucide-react';
import { toast } from 'sonner';
import DonateButton from '../components/DonateButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUBJECTS = {
  'MAT': 'Mathematics',
  'PHY': 'Physics',
  'SCI': 'Science',
  'BIO': 'Biology',
  'ENG': 'English',
  'CHS': 'Chess',
  'CUB': "Rubik's Cube",
  'CON': 'Confidence Club',
  'CAR': 'Career Guidance'
};

const ACADEMIC_SUBJECTS = ['MAT', 'PHY', 'SCI', 'BIO', 'ENG'];
const NON_ACADEMIC_SUBJECTS = ['CHS', 'CUB', 'CON', 'CAR'];

export default function StudentDashboard({ user, logout }) {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchTutors, setBatchTutors] = useState({});
  const [curriculum, setCurriculum] = useState({});
  const [expandedCurriculum, setExpandedCurriculum] = useState({});
  const [loading, setLoading] = useState(true);
  const [availableCurriculum, setAvailableCurriculum] = useState([]);
  
  // Join class state
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const fetchData = async () => {
    try {
      let studentData;
      let batchesRes;
      
      // If studentId is "me" or undefined (direct student login), fetch logged-in student's data
      if (!studentId || studentId === 'me' || user.role === 'student') {
        const [studentRes, batchesData] = await Promise.all([
          axios.get(`${API}/students/me`, { withCredentials: true }),
          axios.get(`${API}/students/me/batches`, { withCredentials: true })
        ]);
        studentData = studentRes.data;
        batchesRes = { data: batchesData.data };
      } else {
        // Parent viewing their kid
        const [studentsRes, allBatchesRes] = await Promise.all([
          axios.get(`${API}/students`, { withCredentials: true }),
          axios.get(`${API}/batches`, { withCredentials: true })
        ]);
        studentData = studentsRes.data.find(s => s.id === studentId);
        batchesRes = allBatchesRes;
        
        if (!studentData) {
          toast.error('Student not found');
          navigate('/dashboard');
          return;
        }
      }
      
      setStudent(studentData);
      
      // For "me", batches are already filtered; for parent view, filter them
      const studentBatches = (!studentId || studentId === 'me' || user.role === 'student')
        ? batchesRes.data 
        : batchesRes.data.filter(batch => batch.student_ids && batch.student_ids.includes(studentId));
      setBatches(studentBatches);

      // Fetch tutor information for each batch
      const tutorData = {};
      for (const batch of studentBatches) {
        try {
          const tutorsRes = await axios.get(`${API}/batches/${batch.id}/tutors`, { withCredentials: true });
          if (tutorsRes.data && tutorsRes.data.length > 0) {
            // Extract tutor and user data from the response
            // API returns: { assignment, tutor, tutor_user }
            tutorData[batch.id] = tutorsRes.data.map(item => ({
              ...item.tutor,
              name: item.tutor_user?.name || item.tutor?.name || 'Unknown',
              email: item.tutor_user?.email || item.tutor?.email,
              photo_url: item.tutor_user?.picture || item.tutor_user?.photo_url || item.tutor?.selfie_url
            }));
          }
        } catch (error) {
          console.error(`Failed to fetch tutors for batch ${batch.id}`);
        }
      }
      setBatchTutors(tutorData);

      // Fetch curriculum for student's subjects
      const curriculumData = {};
      for (const subject of studentData.subjects) {
        try {
          const res = await axios.get(
            `${API}/curriculum?board=${studentData.board}&class_level=${studentData.class_level}&subject=${subject}`,
            { withCredentials: true }
          );
          curriculumData[subject] = (res.data || []).sort((a, b) => {
            // First sort by lesson number
            if (a.topic_number !== b.topic_number) {
              return a.topic_number - b.topic_number;
            }
            // Then sort alphabetically by topic name (handles A, B, C)
            return a.topic_name.localeCompare(b.topic_name);
          });
        } catch (error) {
          console.error(`Failed to fetch curriculum for ${subject}`);
        }
      }
      setCurriculum(curriculumData);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCurriculum = (subject) => {
    setExpandedCurriculum(prev => ({
      ...prev,
      [subject]: !prev[subject]
    }));
  };

  const handleJoinClass = (batch) => {
    setSelectedBatch(batch);
    setShowJoinDialog(true);
  };

  const confirmJoinClass = () => {
    if (selectedBatch && selectedBatch.gmeet_link) {
      window.open(selectedBatch.gmeet_link, '_blank');
      setShowJoinDialog(false);
      setSelectedBatch(null);
    } else {
      toast.error('No meeting link available for this batch');
    }
  };

  // Calculate statistics
  const academicBatches = batches.filter(b => ACADEMIC_SUBJECTS.includes(b.subject));
  const nonAcademicBatches = batches.filter(b => NON_ACADEMIC_SUBJECTS.includes(b.subject));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  if (!student) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img src="/logo.jpg" alt="Rising Stars Nation" className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg object-cover" />
              <span className="text-base sm:text-xl font-bold hidden sm:block" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Rising Stars Nation</span>
              <span className="text-sm font-bold sm:hidden" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>RSN</span>
            </div>
            <div className="flex items-center space-x-2">
              {user.role !== 'student' && (
                <Button data-testid="back-btn" onClick={() => navigate('/dashboard')} variant="outline" size="sm" className="hidden sm:flex">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
              <Button onClick={() => navigate('/profile')} variant="outline" size="sm">
                <User className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">My Profile</span>
              </Button>
              <Button data-testid="logout-btn" onClick={logout} variant="outline" size="sm">
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{student.name}</h1>
          <p className="text-sm sm:text-base text-gray-600">Code: {student.student_code} | Class {student.class_level} | {student.board} Board</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Batches</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{batches.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Academic Batches</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{academicBatches.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Non-Academic Batches</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">{nonAcademicBatches.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Academic Batches */}
        {academicBatches.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Academic Batches</h2>
            <div className="grid gap-4">
              {academicBatches.map(batch => (
                <div
                  key={batch.id}
                  data-testid={`batch-${batch.id}`}
                  className="bg-white rounded-xl shadow-lg p-6"
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900">{batch.batch_code}</h3>
                      <p className="text-sm sm:text-base text-gray-600">{SUBJECTS[batch.subject]} | Status: {batch.status}</p>
                      <p className="text-xs sm:text-sm text-gray-500">Class {batch.class_level} | {batch.board} Board</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <Button 
                        onClick={() => handleJoinClass(batch)} 
                        variant="outline" 
                        size="sm"
                        className="bg-green-50 hover:bg-green-100 w-full sm:w-auto"
                      >
                        <Video className="h-4 w-4 mr-2" />
                        Join Class
                      </Button>
                      <Button 
                        data-testid={`view-logboard-${batch.id}`} 
                        onClick={() => navigate(`/logboard/${batch.id}`)} 
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Log Board
                      </Button>
                    </div>
                  </div>
                  
                  {/* Tutor Information */}
                  {batchTutors[batch.id] && batchTutors[batch.id].length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <GraduationCap className="h-5 w-5 mr-2 text-blue-600" />
                        Your Tutors
                      </h4>
                      <div className="space-y-3">
                        {batchTutors[batch.id].map(tutor => (
                          <div key={tutor.id} className="bg-blue-50 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              {tutor.photo_url ? (
                                <img src={tutor.photo_url} alt={tutor.name} className="w-12 h-12 rounded-full object-cover" />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center">
                                  <GraduationCap className="h-6 w-6 text-blue-600" />
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">{tutor.name}</p>
                                <p className="text-sm text-gray-600">{tutor.tutor_code}</p>
                                {tutor.about_yourself && (
                                  <p className="text-sm text-gray-700 mt-2 italic">"{tutor.about_yourself}"</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Non-Academic Batches */}
        {nonAcademicBatches.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Non-Academic Courses</h2>
            <div className="grid gap-4">
              {nonAcademicBatches.map(batch => (
                <div
                  key={batch.id}
                  data-testid={`batch-${batch.id}`}
                  className="bg-white rounded-xl shadow-lg p-6"
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900">{batch.batch_code}</h3>
                      <p className="text-sm sm:text-base text-gray-600">{SUBJECTS[batch.subject]} | Status: {batch.status}</p>
                      <p className="text-xs sm:text-sm text-gray-500">Class {batch.class_level} | {batch.board} Board</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <Button 
                        onClick={() => handleJoinClass(batch)} 
                        variant="outline" 
                        size="sm"
                        className="bg-green-50 hover:bg-green-100 w-full sm:w-auto"
                      >
                        <Video className="h-4 w-4 mr-2" />
                        Join Class
                      </Button>
                      <Button 
                        data-testid={`view-logboard-${batch.id}`} 
                        onClick={() => navigate(`/logboard/${batch.id}`)} 
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Log Board
                      </Button>
                    </div>
                  </div>
                  
                  {/* Tutor Information */}
                  {batchTutors[batch.id] && batchTutors[batch.id].length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <GraduationCap className="h-5 w-5 mr-2 text-blue-600" />
                        Your Tutors
                      </h4>
                      <div className="space-y-3">
                        {batchTutors[batch.id].map(tutor => (
                          <div key={tutor.id} className="bg-blue-50 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              {tutor.photo_url ? (
                                <img src={tutor.photo_url} alt={tutor.name} className="w-12 h-12 rounded-full object-cover" />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center">
                                  <GraduationCap className="h-6 w-6 text-blue-600" />
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">{tutor.name}</p>
                                <p className="text-sm text-gray-600">{tutor.tutor_code}</p>
                                {tutor.about_yourself && (
                                  <p className="text-sm text-gray-700 mt-2 italic">"{tutor.about_yourself}"</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Batches Message */}
        {batches.length === 0 && (
          <div className="mb-8 bg-white rounded-xl shadow-lg p-8 text-center">
            <p className="text-gray-600">No active batches yet. Batches will be created when 10+ students enroll for the same subject.</p>
          </div>
        )}

        {/* Curriculum */}
        {ACADEMIC_SUBJECTS.some(subj => student.subjects.includes(subj)) && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>📚 My Curriculum</h2>
            <div className="space-y-4">
              {student.subjects.filter(subj => ACADEMIC_SUBJECTS.includes(subj)).map(subject => {
                const curriculumItems = (curriculum[subject] || []).sort((a, b) => {
                  // First sort by lesson number
                  if (a.topic_number !== b.topic_number) {
                    return a.topic_number - b.topic_number;
                  }
                  // Then sort alphabetically by topic name (handles A, B, C)
                  return a.topic_name.localeCompare(b.topic_name);
                });
                const isExpanded = expandedCurriculum[subject];
                
                return (
                  <div key={subject} data-testid={`curriculum-${subject}`} className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <button
                      onClick={() => toggleCurriculum(subject)}
                      className="w-full flex items-center justify-between p-6 bg-gradient-to-r from-blue-50 to-green-50 hover:from-blue-100 hover:to-green-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 bg-gradient-to-br from-blue-600 to-green-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                          {subject}
                        </div>
                        <div className="text-left">
                          <h3 className="text-xl font-bold text-gray-900">{SUBJECTS[subject]}</h3>
                          <p className="text-sm text-gray-600">
                            {curriculumItems.length > 0 
                              ? `${curriculumItems.length} ${curriculumItems.length === 1 ? 'lesson' : 'lessons'}`
                              : 'No curriculum available yet'
                            }
                          </p>
                        </div>
                      </div>
                      <span className="text-gray-500 text-2xl">{isExpanded ? '▼' : '▶'}</span>
                    </button>
                    
                    {isExpanded && (
                      <div className="p-6 bg-white">
                        {curriculumItems.length > 0 ? (
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {curriculumItems.map(item => (
                              <div key={item.id} className="bg-blue-50 rounded-lg p-4 border border-blue-200 hover:border-blue-300 transition-colors">
                                <div className="flex items-start">
                                  <span className="font-semibold text-blue-700 mr-3 text-lg min-w-[40px]">{item.topic_number}.</span>
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900 text-base">{item.topic_name}</p>
                                    {item.description && (
                                      <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-600">Curriculum will be available once uploaded by admin</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Join Class Confirmation Dialog */}
      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Join Class</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">
              Do you want to join the class for <strong>{selectedBatch?.batch_code}</strong>?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              This will open the Google Meet link in a new tab.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJoinDialog(false)}>
              No, Cancel
            </Button>
            <Button 
              onClick={confirmJoinClass}
              className="bg-green-600 hover:bg-green-700"
            >
              Yes, Join Class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
